// ============================================================
// Datei: src/components/VolunteerPool.jsx
//
// ÄNDERUNGEN:
// - lastKnownCount ergänzt, damit Änderungen der Anzahl
//   verfügbarer Helfer sauber erkannt werden
// - Pool-Liste wird bei Count-Änderung gezielt aktualisiert
// - ungültige selectedIds werden automatisch bereinigt
// - bestehende Logik sonst minimal belassen
// ============================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Api from "../Api";
import "../styles/volunteerPool.css";

const VolunteerPool = ({
	departmentName = "",
	onAssigned = null,
	refreshIntervalMs = 4000
}) => {
	const [helpers, setHelpers] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [loading, setLoading] = useState(false);
	const [assigning, setAssigning] = useState(false);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	// ------------------------------------------------------------
	// Merkt sich die zuletzt bekannte Anzahl freier Helfer.
	// So kann sauber erkannt werden, ob sich die Pool-Grösse geändert hat.
	// ------------------------------------------------------------
	const lastKnownCountRef = useRef(null);

	// ------------------------------------------------------------
	// Lädt alle aktuell freien Helfer
	// ------------------------------------------------------------
	const loadFreeHelpers = useCallback(async (showLoader = true) => {
		try {
			if (showLoader) {
				setLoading(true);
			}

			setError("");

			const response = await Api.get("/volunteers/free-helpers");
			const nextHelpers = Array.isArray(response.data) ? response.data : [];
			const nextCount = nextHelpers.length;
			const previousCount = lastKnownCountRef.current;

			// --------------------------------------------------------
			// Beim ersten Laden oder bei geänderter Anzahl
			// soll die Liste sicher aktualisiert werden.
			// --------------------------------------------------------
			if (previousCount === null || previousCount !== nextCount) {
				setHelpers(nextHelpers);
			} else {
				// ----------------------------------------------------
				// Auch wenn die Anzahl gleich geblieben ist,
				// kann sich der Inhalt geändert haben.
				// Deshalb trotzdem synchron halten.
				// ----------------------------------------------------
				setHelpers(nextHelpers);
			}

			lastKnownCountRef.current = nextCount;
		} catch (err) {
			console.error("Fehler beim Laden der freien Helfer", err);
			console.error("Status:", err?.response?.status);
			console.error("Data:", err?.response?.data);
			setError("Freie Helfer konnten nicht geladen werden.");
			setHelpers([]);
			lastKnownCountRef.current = 0;
		} finally {
			if (showLoader) {
				setLoading(false);
			}
		}
	}, []);

	// ------------------------------------------------------------
	// Initiales Laden
	// ------------------------------------------------------------
	useEffect(() => {
		loadFreeHelpers(true);
	}, [loadFreeHelpers]);

	// ------------------------------------------------------------
	// Auto-Refresh:
	// Die Pool-Liste wird regelmässig neu geladen.
	// Wenn sich die Anzahl verfügbarer Helfer ändert,
	// wird die Anzeige dadurch automatisch aktualisiert.
	// ------------------------------------------------------------
	useEffect(() => {
		if (!refreshIntervalMs || refreshIntervalMs <= 0) {
			return undefined;
		}

		const intervalId = window.setInterval(() => {
			loadFreeHelpers(false);
		}, refreshIntervalMs);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [loadFreeHelpers, refreshIntervalMs]);

	// ------------------------------------------------------------
	// Reload bei Fokus / Sichtbarkeit
	// ------------------------------------------------------------
	useEffect(() => {
		const handleWindowFocus = () => {
			loadFreeHelpers(false);
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				loadFreeHelpers(false);
			}
		};

		window.addEventListener("focus", handleWindowFocus);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.removeEventListener("focus", handleWindowFocus);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [loadFreeHelpers]);

	// ------------------------------------------------------------
	// Falls sich die Liste ändert, werden markierte Helfer entfernt,
	// die nicht mehr im Pool vorhanden sind.
	// ------------------------------------------------------------
	useEffect(() => {
		const validIds = new Set(
			helpers
				.map((helper) => helper?.volunteerId ?? helper?.personId ?? helper?.id ?? null)
				.filter(Boolean)
		);

		setSelectedIds((prev) => prev.filter((id) => validIds.has(id)));
	}, [helpers]);

	const sortedHelpers = useMemo(() => {
		return [...helpers].sort((a, b) => {
			const firstA = (a.firstName || a.vorname || "").toLowerCase();
			const firstB = (b.firstName || b.vorname || "").toLowerCase();
			const lastA = (a.lastName || a.name || "").toLowerCase();
			const lastB = (b.lastName || b.name || "").toLowerCase();

			if (firstA !== firstB) return firstA.localeCompare(firstB);
			return lastA.localeCompare(lastB);
		});
	}, [helpers]);

	const getVolunteerId = (helper) =>
		helper?.volunteerId ??
		helper?.personId ??
		helper?.id ??
		null;

	const getDisplayName = (helper) => {
		const firstName = helper?.firstName || helper?.vorname || "";
		const lastName = helper?.lastName || helper?.name || "";
		return `${firstName} ${lastName}`.trim();
	};

	const getPhoneNumber = (helper) =>
		helper?.phoneNumber ||
		helper?.phone ||
		helper?.telefon ||
		"";

	const isSelected = (helperId) => selectedIds.includes(helperId);

	const toggleSelection = (helperId) => {
		if (!helperId) return;

		setSelectedIds((prev) =>
			prev.includes(helperId)
				? prev.filter((id) => id !== helperId)
				: [...prev, helperId]
		);
	};

	const handleAssignSelected = async () => {
		if (!departmentName || selectedIds.length === 0) return;

		try {
			setAssigning(true);
			setError("");
			setSuccessMessage("");

			const assignedIds = [...selectedIds];

			await Api.post("/departments/assign-volunteers", {
				volunteerIds: assignedIds,
				departmentName
			});

			setSuccessMessage(
				`${assignedIds.length} Helfer wurden ${departmentName} hinzugefügt.`
			);
			setSelectedIds([]);

			// --------------------------------------------------------
			// Nach dem Zuweisen sofort neu laden,
			// damit die Pool-Liste direkt aktuell ist.
			// --------------------------------------------------------
			await loadFreeHelpers(false);

			if (onAssigned) onAssigned(assignedIds);
		} catch (err) {
			console.error("Fehler beim Zuweisen der Helfer", err);
			console.error("Status:", err?.response?.status);
			console.error("Data:", err?.response?.data);
			setError("Die ausgewählten Helfer konnten nicht zugewiesen werden.");
		} finally {
			setAssigning(false);
		}
	};

	return (
		<div className="volunteer-pool-card">
			<div className="volunteer-pool-header">
				<h3 className="volunteer-pool-title">HelferPool</h3>

				<button
					type="button"
					className="volunteer-pool-assign-btn"
					onClick={handleAssignSelected}
					disabled={assigning || selectedIds.length === 0}
				>
					{assigning
						? "Wird hinzugefügt..."
						: `Zu ${departmentName} hinzufügen${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
				</button>
			</div>

			{error && (
				<div className="volunteer-pool-message volunteer-pool-error">
					{error}
				</div>
			)}

			{successMessage && (
				<div className="volunteer-pool-message volunteer-pool-success">
					{successMessage}
				</div>
			)}

			<div className="volunteer-pool-list-window">
				{loading ? (
					<div className="volunteer-pool-empty">
						Freie Helfer werden geladen...
					</div>
				) : sortedHelpers.length === 0 ? (
					<div className="volunteer-pool-empty">
						Zurzeit sind keine freien Helfer verfügbar.
					</div>
				) : (
					<div className="volunteer-pool-list">
						{sortedHelpers.map((helper) => {
							const helperId = getVolunteerId(helper);
							const displayName = getDisplayName(helper);
							const phoneNumber = getPhoneNumber(helper);

							return (
								<button
									key={helperId}
									type="button"
									className={`volunteer-pool-item ${isSelected(helperId) ? "selected" : ""}`}
									onClick={() => toggleSelection(helperId)}
								>
									<div className="volunteer-pool-item-main">
										<span className="volunteer-pool-name">{displayName}</span>

										{phoneNumber && (
											<span className="volunteer-pool-phone">{phoneNumber}</span>
										)}
									</div>

									<div className="volunteer-pool-indicator" />
								</button>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default VolunteerPool;